import { resetCustomerUserPasswordApi } from "@/api/services/main/customer-user";
import { TCustomerUserDetailDto } from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { Button } from "@/components/elements/Button";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { ReadonlyField } from "@/features/customer-user/components/detail/ReadonlyField";
import { USER_STATUS_OPTIONS } from "@/features/customer-user/constant/options";
import { canResetPassword } from "@/features/customer-user/constant/statusHelpers";
import { TCustomerUserForm } from "@/features/customer-user/types/customerUserForm";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useDialog } from "@/hooks/useDialog";
import { text } from "@/theme/colors";
import { USER_ACCOUNT_STATUS, USER_ACCOUNT_STATUS_LABEL, USER_STATUS_LABEL, USER_STATUS, TUserAccountStatus } from "@/types/enum";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Control } from "react-hook-form";

type TProps = {
  control: Control<TCustomerUserForm>;
  user?: TCustomerUserDetailDto;
};

export const CustomerUserStatusFields: FC<TProps> = ({ control, user }) => {
  const isCreate = !user;
  const accountStatus = user?.accountStatus ?? USER_ACCOUNT_STATUS.INVITATION_SENT;
  const accountStatusLabel = USER_ACCOUNT_STATUS_LABEL[accountStatus as TUserAccountStatus] ?? accountStatus;
  const queryClient = useQueryClient();
  const passwordResetDialog = useDialog();

  const handleResetPassword = async () => {
    if (!user) return;
    const confirmed = await passwordResetDialog.show();
    if (!confirmed) return;
    try {
      await resetCustomerUserPasswordApi(user.id);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_DETAIL, String(user.id)] });
      toast.success("パスワードリセットメールを送信しました。");
    } catch (error) {
      showError(error);
    }
  };

  return (
    <InnerBox title="ユーザーの状態" padding="24px">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap="24px" flexWrap="wrap">
        <Stack direction="row" gap="48px" flexWrap="wrap" alignItems="flex-start" flex={1}>
          {isCreate ? (
            <ReadonlyField label="ステータス" required value={USER_STATUS_LABEL[USER_STATUS.ACTIVE]} />
          ) : (
            <Stack flex={1} minWidth={0}>
              <FormField control={control} name="status">
                <Select options={USER_STATUS_OPTIONS} clearable={false} />
              </FormField>
            </Stack>
          )}
          <ReadonlyField label="認証ステータス" value={accountStatusLabel} />
        </Stack>
        <Button
          variant="ghost"
          size="sm"
          sx={{ width: "158px" }}
          type="button"
          disabled={!user || !canResetPassword(user.hasPassword, user.accountStatus)}
          onClick={handleResetPassword}
        >
          パスワードリセット
        </Button>
      </Stack>

      {user && (
        <Modal
          isOpen={passwordResetDialog.open}
          title="パスワードリセット"
          labelOk="リセットする"
          labelClose="キャンセル"
          onOk={passwordResetDialog.confirm}
          onClose={passwordResetDialog.cancel}
        >
          <Typography variant="cap12" color={text.secondary} py={2} component="div">
            登録済みパスワードがリセットされます。再登録のためのURLが本人にメールで通知されます。
            <br />
            続行してよろしいですか。
          </Typography>
        </Modal>
      )}
    </InnerBox>
  );
};
